import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import GridViewIcon from '@mui/icons-material/GridView';
import HomeIcon from '@mui/icons-material/Home';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PushPinIcon from '@mui/icons-material/PushPin';
import SellIcon from '@mui/icons-material/Sell';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import UpdateIcon from '@mui/icons-material/Update';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import AppearanceDialog from '../components/AppearanceDialog';
import BreadcrumbNav from '../components/BreadcrumbNav';
import ConfirmDialog from '../components/ConfirmDialog';
import DownloadDialog from '../components/DownloadDialog';
import ExplorerItems from '../components/ExplorerItems';
import MoveDialog from '../components/MoveDialog';
import NameDialog from '../components/NameDialog';
import TagDialog from '../components/TagDialog';
import WorkspaceSettingsDialog from '../components/WorkspaceSettingsDialog';
import WorkspaceSearch from '../components/WorkspaceSearch';
import { useWorkspace } from '../store/WorkspaceContext';
import {
  DEFAULT_FOLDER_TAG_COLOR,
  GRID_SIZE_OPTIONS,
  ROOT_FOLDER_ID,
  SORT_OPTIONS,
  TAG_COLORS,
} from '../utils/constants';
import { buildFolderPath, getDescendantFolderIds, getFileDisplayName } from '../utils/workspace';

function getEntryName(entry) {
  return entry.type === 'file' ? getFileDisplayName(entry.item) : entry.item.name;
}

function getEntryTime(entry, field) {
  const timestamp = Date.parse(entry.item[field] || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function getEntryKey(entry) {
  return `${entry.type}:${entry.item.id}`;
}

function sortEntries(entries, sortMode = 'nameAsc') {
  return entries.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === 'folder' ? -1 : 1;
    }

    const aName = getEntryName(a);
    const bName = getEntryName(b);

    if (sortMode === 'nameDesc') {
      return bName.localeCompare(aName);
    }

    if (sortMode === 'updatedDesc') {
      return getEntryTime(b, 'updatedAt') - getEntryTime(a, 'updatedAt') || aName.localeCompare(bName);
    }

    if (sortMode === 'updatedAsc') {
      return getEntryTime(a, 'updatedAt') - getEntryTime(b, 'updatedAt') || aName.localeCompare(bName);
    }

    if (sortMode === 'createdDesc') {
      return getEntryTime(b, 'createdAt') - getEntryTime(a, 'createdAt') || aName.localeCompare(bName);
    }

    if (sortMode === 'createdAsc') {
      return getEntryTime(a, 'createdAt') - getEntryTime(b, 'createdAt') || aName.localeCompare(bName);
    }

    return aName.localeCompare(bName);
  });
}

function GridSizeMark({ index, selected }) {
  return (
    <Box
      sx={{
        width: 12 + index * 3,
        height: 12 + index * 3,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 0.25,
      }}
    >
      {[0, 1, 2, 3].map((item) => (
        <Box
          key={item}
          sx={{
            borderRadius: 0.25,
            bgcolor: selected ? 'primary.main' : 'text.secondary',
          }}
        />
      ))}
    </Box>
  );
}

function SortMark({ sortMode }) {
  if (sortMode === 'nameAsc' || sortMode === 'nameDesc') {
    return (
      <Typography component="span" fontWeight={900} sx={{ fontSize: 10, lineHeight: 1 }}>
        {sortMode === 'nameAsc' ? 'A-Z' : 'Z-A'}
      </Typography>
    );
  }

  const ascending = sortMode.endsWith('Asc');
  const BaseIcon = sortMode.startsWith('created') ? AddCircleIcon : UpdateIcon;
  const ArrowIcon = ascending ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box sx={{ position: 'relative', width: 22, height: 22 }}>
      <BaseIcon sx={{ position: 'absolute', left: 0, top: 1, fontSize: 18 }} />
      <ArrowIcon sx={{ position: 'absolute', right: -2, bottom: -1, fontSize: 13 }} />
    </Box>
  );
}

function getFirstRowLimit({ containerWidth, gridSize, viewMode }) {
  if (viewMode === 'list') {
    return 3;
  }

  const gap = 16;
  const size = GRID_SIZE_OPTIONS.find((option) => option.value === gridSize) || GRID_SIZE_OPTIONS[2];
  return Math.max(1, Math.floor((containerWidth + gap) / (size.minWidth + gap)));
}

function FavoritesSection({
  entries,
  viewMode,
  gridSize,
  open,
  onToggleOpen,
  onOpen,
  getMenuProps,
  selectionMode,
  selectedKeySet,
  onEntryLongPress,
  onToggleSelection,
  onVisibleEntriesChange,
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [entries.length, gridSize, viewMode]);

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(node.getBoundingClientRect().width || 1200);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const firstRowLimit = getFirstRowLimit({ containerWidth, gridSize, viewMode });
  const hasOverflow = entries.length > firstRowLimit;
  const visibleEntries = useMemo(
    () => (expanded || !hasOverflow ? entries : entries.slice(0, firstRowLimit)),
    [entries, expanded, firstRowLimit, hasOverflow]
  );

  useEffect(() => {
    onVisibleEntriesChange?.(open ? visibleEntries : []);
  }, [onVisibleEntriesChange, open, visibleEntries]);

  return (
    <Stack spacing={1.25} ref={containerRef}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}
      >
        <Button
          startIcon={
            <ExpandMoreIcon
              sx={{
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 160ms ease',
              }}
            />
          }
          onClick={onToggleOpen}
          sx={{ color: 'text.primary', px: 0.75, userSelect: 'none' }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <StarIcon color="warning" fontSize="small" />
            <Typography variant="h6" fontWeight={900} noWrap>
              Favorites
            </Typography>
          </Stack>
        </Button>
        {open && hasOverflow ? (
          <Button size="small" onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Show less' : `Show more (${entries.length - firstRowLimit})`}
          </Button>
        ) : null}
      </Stack>
      <Collapse in={open} timeout={180} unmountOnExit>
        <ExplorerItems
          entries={visibleEntries}
          viewMode={viewMode}
          gridSize={gridSize}
          onOpen={onOpen}
          getMenuProps={getMenuProps}
          selectionMode={selectionMode}
          selectedKeySet={selectedKeySet}
          onEntryLongPress={onEntryLongPress}
          onToggleSelection={onToggleSelection}
        />
      </Collapse>
    </Stack>
  );
}

export default function ExplorerPage() {
  const navigate = useNavigate();
  const {
    folders,
    files,
    settings,
    currentFolder,
    currentFolderId,
    setCurrentFolderId,
    updateSettings,
    createFolder,
    createFile,
    renameFolder,
    renameFile,
    deleteFolder,
    deleteFile,
    moveFolder,
    moveFile,
    moveEntries,
    updateFolder,
    setFolderTag,
    setFolderTags,
    updateFile,
    setEntriesFavorite,
    updateFileAppearance,
    resetFileAppearanceToDefault,
    deleteEntries,
    validateName,
  } = useWorkspace();

  const [nameDialog, setNameDialog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [downloadTarget, setDownloadTarget] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);
  const [appearanceFileId, setAppearanceFileId] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [gridSizePickerOpen, setGridSizePickerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectionModeForced, setSelectionModeForced] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectionAnchorKey, setSelectionAnchorKey] = useState(null);
  const [visibleFavoriteEntries, setVisibleFavoriteEntries] = useState([]);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);

  const appearanceFile = files.find((file) => file.id === appearanceFileId);
  const activeFilter = settings.explorerFilter || 'all';
  const activeSort = settings.explorerSort || 'nameAsc';
  const activeSortOption =
    SORT_OPTIONS.find((option) => option.value === activeSort) || SORT_OPTIONS[0];
  const defaultTagColor = settings.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR;
  const availableTagColors = TAG_COLORS.filter((tag) => tag.value !== defaultTagColor);
  const activeTag = availableTagColors.find((tag) => tag.value === activeFilter);
  const favoriteFilterActive = activeFilter === 'favorites';
  const filesFilterActive = activeFilter === 'files';

  useEffect(() => {
    if (settings.viewMode === 'list') {
      setGridSizePickerOpen(false);
    }
  }, [settings.viewMode]);

  useEffect(() => {
    if (!gridSizePickerOpen) {
      return undefined;
    }

    const timer = window.setTimeout(() => setGridSizePickerOpen(false), 5000);
    return () => window.clearTimeout(timer);
  }, [gridSizePickerOpen]);

  const favoriteEntries = useMemo(() => {
    const folderEntries = folders
      .filter((folder) => folder.id !== ROOT_FOLDER_ID && folder.isFavorite)
      .map((folder) => ({
        type: 'folder',
        item: folder,
        subtitle: buildFolderPath(folders, folder.parentId || ROOT_FOLDER_ID),
      }));
    const fileEntries = files
      .filter((file) => file.isFavorite)
      .map((file) => ({
        type: 'file',
        item: file,
        subtitle: buildFolderPath(folders, file.parentFolderId),
      }));

    return sortEntries([...folderEntries, ...fileEntries], activeSort);
  }, [activeSort, files, folders]);

  const entries = useMemo(() => {
    if (activeFilter === 'favorites') {
      return favoriteEntries;
    }

    if (activeFilter === 'files') {
      return sortEntries(
        files.map((file) => ({
          type: 'file',
          item: file,
          subtitle: buildFolderPath(folders, file.parentFolderId),
        })),
        activeSort
      );
    }

    if (activeFilter !== 'all') {
      return sortEntries(
        folders
          .filter((folder) => folder.id !== ROOT_FOLDER_ID && folder.tagColor === activeFilter)
          .map((folder) => ({
            type: 'folder',
            item: folder,
            subtitle: buildFolderPath(folders, folder.parentId || ROOT_FOLDER_ID),
          })),
        activeSort
      );
    }

    const folderEntries = folders
      .filter((folder) => folder.parentId === currentFolderId)
      .map((folder) => ({ type: 'folder', item: folder }));
    const fileEntries = files
      .filter((file) => file.parentFolderId === currentFolderId)
      .map((file) => ({ type: 'file', item: file }));

    return sortEntries([...folderEntries, ...fileEntries], activeSort);
  }, [activeFilter, activeSort, currentFolderId, favoriteEntries, files, folders]);

  const showFavoritesSection =
    currentFolderId === ROOT_FOLDER_ID && activeFilter === 'all' && favoriteEntries.length > 0;
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const selectionMode = selectionModeForced || selectedKeys.length > 0;
  const visibleEntries = useMemo(() => {
    const uniqueEntries = new Map();

    [...visibleFavoriteEntries, ...entries].forEach((entry) => {
      uniqueEntries.set(getEntryKey(entry), entry);
    });

    return [...uniqueEntries.values()];
  }, [entries, visibleFavoriteEntries]);
  const visibleEntryMap = useMemo(
    () => new Map(visibleEntries.map((entry) => [getEntryKey(entry), entry])),
    [visibleEntries]
  );
  const selectedEntries = useMemo(
    () => selectedKeys.map((key) => visibleEntryMap.get(key)).filter(Boolean),
    [selectedKeys, visibleEntryMap]
  );
  const selectedFolderEntries = useMemo(
    () => selectedEntries.filter((entry) => entry.type === 'folder'),
    [selectedEntries]
  );
  const selectedFileEntries = useMemo(
    () => selectedEntries.filter((entry) => entry.type === 'file'),
    [selectedEntries]
  );
  const visibleEntryKeys = useMemo(
    () => visibleEntries.map((entry) => getEntryKey(entry)),
    [visibleEntries]
  );
  const allVisibleSelected =
    visibleEntryKeys.length > 0 && visibleEntryKeys.every((entryKey) => selectedKeySet.has(entryKey));
  const batchItems = useMemo(
    () =>
      selectedEntries.map((entry) => ({
        type: entry.type,
        id: entry.item.id,
      })),
    [selectedEntries]
  );
  const allSelectedAreFavorites =
    selectedEntries.length > 0 && selectedEntries.every((entry) => entry.item.isFavorite);
  const bulkMoveDisabledIds = useMemo(() => {
    const disabledIds = new Set();

    selectedFolderEntries.forEach((entry) => {
      disabledIds.add(entry.item.id);
      getDescendantFolderIds(folders, entry.item.id).forEach((folderId) => {
        disabledIds.add(folderId);
      });
    });

    return disabledIds;
  }, [folders, selectedFolderEntries]);
  const bulkMoveInitialFolderId = useMemo(() => {
    const parentIds = new Set(
      selectedEntries.map((entry) =>
        entry.type === 'folder' ? entry.item.parentId || ROOT_FOLDER_ID : entry.item.parentFolderId
      )
    );

    if (parentIds.size === 1) {
      return [...parentIds][0];
    }

    return ROOT_FOLDER_ID;
  }, [selectedEntries]);
  const bulkTagValue = useMemo(() => {
    if (!selectedFolderEntries.length) {
      return defaultTagColor;
    }

    const tagValues = new Set(
      selectedFolderEntries.map((entry) => entry.item.tagColor || defaultTagColor)
    );

    return tagValues.size === 1 ? [...tagValues][0] : defaultTagColor;
  }, [defaultTagColor, selectedFolderEntries]);

  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
    setSelectionModeForced(false);
    setSelectionAnchorKey(null);
  }, []);

  useEffect(() => {
    if (!showFavoritesSection) {
      setVisibleFavoriteEntries([]);
    }
  }, [showFavoritesSection]);

  useEffect(() => {
    clearSelection();
  }, [activeFilter, clearSelection, currentFolderId]);

  useEffect(() => {
    setSelectedKeys((previous) => previous.filter((entryKey) => visibleEntryMap.has(entryKey)));
    setSelectionAnchorKey((previous) => (previous && visibleEntryMap.has(previous) ? previous : null));
  }, [visibleEntryMap]);

  const openEntry = useCallback((entry) => {
    if (entry.type === 'folder') {
      if (activeFilter !== 'all') {
        updateSettings({ explorerFilter: 'all' });
      }
      setCurrentFolderId(entry.item.id);
      return;
    }

    setCurrentFolderId(entry.item.parentFolderId);
    navigate(`/file/${entry.item.id}`);
  }, [activeFilter, navigate, setCurrentFolderId, updateSettings]);

  const toggleEntrySelection = useCallback((entry) => {
    const entryKey = getEntryKey(entry);

    setSelectionModeForced(true);
    setSelectedKeys((previous) =>
      previous.includes(entryKey)
        ? previous.filter((key) => key !== entryKey)
        : [...previous, entryKey]
    );
    setSelectionAnchorKey(entryKey);
  }, []);

  const handleEntryAction = useCallback((entry, modifiers = {}, scopeEntries = []) => {
    const entryKey = getEntryKey(entry);
    const multiSelect = modifiers.metaKey || modifiers.ctrlKey;

    if (modifiers.shiftKey) {
      const scopeKeys = scopeEntries.map((scopeEntry) => getEntryKey(scopeEntry));
      const anchorKey =
        selectionAnchorKey && scopeKeys.includes(selectionAnchorKey)
          ? selectionAnchorKey
          : scopeKeys.find((key) => selectedKeySet.has(key));

      setSelectionModeForced(true);
      setSelectedKeys((previous) => {
        if (!anchorKey || !scopeKeys.includes(anchorKey)) {
          return previous.includes(entryKey) ? previous : [...previous, entryKey];
        }

        const anchorIndex = scopeKeys.indexOf(anchorKey);
        const targetIndex = scopeKeys.indexOf(entryKey);
        const [start, end] =
          anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
        const nextKeys = scopeKeys.slice(start, end + 1);

        return [...new Set([...previous, ...nextKeys])];
      });
      setSelectionAnchorKey(entryKey);
      return;
    }

    if (multiSelect) {
      setSelectionModeForced(true);
      setSelectedKeys((previous) =>
        previous.includes(entryKey)
          ? previous.filter((key) => key !== entryKey)
          : [...previous, entryKey]
      );
      setSelectionAnchorKey(entryKey);
      return;
    }

    if (selectionMode) {
      setSelectedKeys((previous) =>
        previous.includes(entryKey)
          ? previous.filter((key) => key !== entryKey)
          : [...previous, entryKey]
      );
      setSelectionAnchorKey(entryKey);
      return;
    }

    openEntry(entry);
  }, [openEntry, selectedKeySet, selectionAnchorKey, selectionMode]);

  const handleSelectAllVisible = () => {
    if (!visibleEntryKeys.length) {
      return;
    }

    setSelectionModeForced(true);
    setSelectedKeys(visibleEntryKeys);
    setSelectionAnchorKey(visibleEntryKeys[0]);
  };

  const handleBulkFavorite = () => {
    if (!batchItems.length) {
      return;
    }

    setEntriesFavorite(batchItems, !allSelectedAreFavorites);
  };

  const handleBulkDelete = () => {
    if (!batchItems.length) {
      return;
    }

    deleteEntries(batchItems);
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const handleBulkMove = (destinationId) => {
    if (!batchItems.length) {
      return;
    }

    moveEntries(batchItems, destinationId);
    clearSelection();
    setBulkMoveOpen(false);
  };

  const openNameDialog = (mode, entry = null) => {
    const isFile = mode.includes('File');
    const parentId =
      entry?.type === 'file'
        ? entry.item.parentFolderId
        : entry?.type === 'folder'
          ? entry.item.parentId
          : currentFolderId;

    setNameDialog({
      mode,
      entry,
      type: isFile ? 'file' : 'folder',
      parentId,
      title:
        mode === 'createFolder'
          ? 'New Folder'
          : mode === 'createFile'
            ? 'New File'
            : isFile
              ? 'Rename File'
              : 'Rename Folder',
      label: isFile ? 'File name' : 'Folder name',
      initialValue: entry ? entry.item.name : '',
    });
  };

  const handleNameSubmit = (name) => {
    if (!nameDialog) {
      return;
    }

    if (nameDialog.mode === 'createFolder') {
      createFolder(name, currentFolderId);
    } else if (nameDialog.mode === 'createFile') {
      createFile(name, currentFolderId);
    } else if (nameDialog.mode === 'renameFolder') {
      renameFolder(nameDialog.entry.item.id, name);
    } else if (nameDialog.mode === 'renameFile') {
      renameFile(nameDialog.entry.item.id, name);
    }
  };

  const validateDialogName = (name) => {
    if (!nameDialog) {
      return '';
    }

    return validateName({
      type: nameDialog.type,
      name,
      parentId: nameDialog.parentId,
      excludeId: nameDialog.entry?.item.id,
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) {
      return;
    }

    if (deleteTarget.type === 'folder') {
      deleteFolder(deleteTarget.item.id);
    } else {
      deleteFile(deleteTarget.item.id);
    }
  };

  const getMenuProps = (entry) => ({
    onRename: () =>
      openNameDialog(entry.type === 'file' ? 'renameFile' : 'renameFolder', entry),
    onDelete: () => setDeleteTarget(entry),
    onMove: () => setMoveTarget(entry),
    onTag: () => setTagTarget(entry.item),
    onAppearance: () => setAppearanceFileId(entry.item.id),
    onDownload: () => setDownloadTarget(entry),
    onFavorite: () => {
      if (entry.type === 'folder') {
        updateFolder(entry.item.id, { isFavorite: !entry.item.isFavorite });
      } else {
        updateFile(entry.item.id, { isFavorite: !entry.item.isFavorite });
      }
    },
  });

  const selectTagFilter = (value) => {
    updateSettings({ explorerFilter: value });
    setTagMenuAnchor(null);
  };

  const selectSort = (value) => {
    updateSettings({ explorerSort: value });
    setSortMenuAnchor(null);
  };

  const toggleFilter = (value) => {
    updateSettings({ explorerFilter: activeFilter === value ? 'all' : value });
  };

  const handleGridViewClick = () => {
    if (settings.viewMode !== 'grid') {
      updateSettings({ viewMode: 'grid' });
      setGridSizePickerOpen(false);
      return;
    }

    setGridSizePickerOpen((value) => !value);
  };

  const sectionTitle =
    activeFilter === 'all'
      ? 'All Items'
      : activeFilter === 'favorites'
        ? 'Favorite Items'
        : activeFilter === 'files'
          ? 'Files'
          : 'Tagged Folders';
  const filterTooltip = activeTag
    ? `Tag: ${activeTag.label}`
    : favoriteFilterActive
      ? 'Favorites only'
      : filesFilterActive
        ? 'Files only'
        : 'Filter by tag';
  const filterCrumb = activeTag
    ? {
        label: activeTag.label,
        icon: (
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: activeTag.value,
              flexShrink: 0,
            }}
          />
        ),
      }
    : favoriteFilterActive
      ? { label: 'Favorites', icon: <StarIcon color="warning" sx={{ fontSize: 17 }} /> }
      : filesFilterActive
        ? { label: 'Files', icon: <InsertDriveFileIcon color="primary" sx={{ fontSize: 17 }} /> }
        : null;

  return (
    <AppLayout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                minWidth: 0,
                width: '100%',
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                  height: 42,
                  userSelect: 'none',
                }}
              >
                <Tooltip title={currentFolder?.parentId ? 'Back to parent folder' : 'At root folder'}>
                  <span>
                    <IconButton
                      aria-label="Back to parent folder"
                      disabled={!currentFolder?.parentId}
                      onClick={() => setCurrentFolderId(currentFolder.parentId)}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 0.75,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={currentFolderId !== ROOT_FOLDER_ID ? 'Go to home' : 'At home'}>
                  <span>
                    <IconButton
                      aria-label="Go to home"
                      disabled={currentFolderId === ROOT_FOLDER_ID}
                      onClick={() => setCurrentFolderId(ROOT_FOLDER_ID)}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 0.75,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <HomeIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Paper>
              <BreadcrumbNav
                filterCrumb={filterCrumb}
                onFilterHomeClick={() => {
                  updateSettings({ explorerFilter: 'all' });
                  setCurrentFolderId(ROOT_FOLDER_ID);
                }}
              />
              <WorkspaceSearch />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
            }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 0.75, sm: 1 }}
              sx={{
                alignItems: 'center',
                minWidth: 0,
                userSelect: 'none',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Tooltip title="Workspace settings">
                <IconButton
                  aria-label="Workspace settings"
                  onClick={() => setSettingsOpen(true)}
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    flexShrink: 0,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={selectionMode ? 'Exit selection mode' : 'Select items'}>
                <IconButton
                  aria-label="Select items"
                  onClick={() => {
                    if (selectionMode) {
                      clearSelection();
                      return;
                    }

                    setSelectionModeForced(true);
                  }}
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    flexShrink: 0,
                    borderRadius: 1,
                    border: 1,
                    borderColor: selectionMode ? 'primary.main' : 'divider',
                    color: selectionMode ? 'primary.main' : 'text.secondary',
                    bgcolor: selectionMode ? 'action.selected' : 'background.paper',
                    '&:hover': {
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CheckBoxOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                startIcon={<CreateNewFolderIcon />}
                variant="contained"
                onClick={() => openNameDialog('createFolder')}
                sx={{
                  flex: { xs: 1, sm: '0 0 auto' },
                  minWidth: 0,
                  height: { xs: 36, sm: 40 },
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: 12, sm: 14 },
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0.5, sm: 1 },
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  New&nbsp;
                </Box>
                Folder
              </Button>
              <Button
                startIcon={<NoteAddIcon />}
                variant="outlined"
                onClick={() => openNameDialog('createFile')}
                sx={{
                  flex: { xs: 1, sm: '0 0 auto' },
                  minWidth: 0,
                  height: { xs: 36, sm: 40 },
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: 12, sm: 14 },
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0.5, sm: 1 },
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  New&nbsp;
                </Box>
                File
              </Button>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
            >
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  flexWrap: 'wrap',
                  userSelect: 'none',
                }}
              >
                <Tooltip title={filesFilterActive ? 'Show all items' : 'Files only'}>
                  <IconButton
                    aria-label="Files only"
                    onClick={() => toggleFilter('files')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 0.75,
                      color: filesFilterActive ? 'primary.main' : 'text.secondary',
                      bgcolor: filesFilterActive ? 'action.selected' : 'transparent',
                    }}
                  >
                    <InsertDriveFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={favoriteFilterActive ? 'Show all items' : 'Favorites only'}>
                  <IconButton
                    aria-label="Favorites only"
                    onClick={() => toggleFilter('favorites')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 0.75,
                      color: favoriteFilterActive ? 'primary.main' : 'text.secondary',
                      bgcolor: favoriteFilterActive ? 'action.selected' : 'transparent',
                    }}
                  >
                    <StarIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={filterTooltip}>
                  <IconButton
                    aria-label="Filter by tag"
                    onClick={(event) => setTagMenuAnchor(event.currentTarget)}
                    sx={{
                      position: 'relative',
                      width: 36,
                      height: 36,
                      borderRadius: 0.75,
                      color: activeTag ? 'primary.main' : 'text.secondary',
                      bgcolor: activeTag ? 'action.selected' : 'transparent',
                    }}
                  >
                    <SellIcon fontSize="small" />
                    {activeTag ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 6,
                          bottom: 6,
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          bgcolor: activeTag.value,
                          border: 1,
                          borderColor: 'background.paper',
                        }}
                      />
                    ) : null}
                  </IconButton>
                </Tooltip>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.25 }} />
                <Tooltip title={`Sort: ${activeSortOption.label}`}>
                  <IconButton
                    aria-label="Sort items"
                    onClick={(event) => setSortMenuAnchor(event.currentTarget)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 0.75,
                      color: activeSort !== 'nameAsc' ? 'primary.main' : 'text.secondary',
                      bgcolor: activeSort !== 'nameAsc' ? 'action.selected' : 'transparent',
                    }}
                  >
                    <SortMark sortMode={activeSort} />
                  </IconButton>
                </Tooltip>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.25 }} />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={settings.viewMode}
                  aria-label="View mode"
                  sx={{
                    '& .MuiToggleButton-root': {
                      width: 36,
                      height: 36,
                      borderRadius: 0.75,
                      border: 0,
                    },
                  }}
                >
                  <ToggleButton value="grid" aria-label="Grid view" onClick={handleGridViewClick}>
                    <GridViewIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton
                    value="list"
                    aria-label="List view"
                    onClick={() => {
                      updateSettings({ viewMode: 'list' });
                      setGridSizePickerOpen(false);
                    }}
                  >
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>

                {settings.viewMode === 'grid' && gridSizePickerOpen ? (
                  <>
                    <Divider flexItem orientation="vertical" sx={{ mx: 0.25 }} />
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={settings.gridSize}
                      onChange={(_, nextSize) => {
                        if (nextSize) {
                          updateSettings({ gridSize: nextSize });
                          setGridSizePickerOpen(false);
                        }
                      }}
                      aria-label="Grid size"
                      sx={{
                        '& .MuiToggleButton-root': {
                          width: 36,
                          height: 36,
                          borderRadius: 0.75,
                          border: 0,
                        },
                      }}
                    >
                      {GRID_SIZE_OPTIONS.map((option, index) => (
                        <ToggleButton
                          key={option.value}
                          value={option.value}
                          aria-label={`${option.label} grid size`}
                        >
                          <GridSizeMark
                            index={index}
                            selected={settings.gridSize === option.value}
                          />
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </>
                ) : null}
              </Paper>
              <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={() => setSortMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    selected={activeSort === option.value}
                    onClick={() => selectSort(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={tagMenuAnchor}
                open={Boolean(tagMenuAnchor)}
                onClose={() => setTagMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem selected={activeFilter === 'all'} onClick={() => selectTagFilter('all')}>
                  All items
                </MenuItem>
                <Divider />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 38px)',
                    gap: 0.75,
                    p: 1,
                  }}
                >
                  {availableTagColors.map((tag) => (
                    <Tooltip key={tag.value} title={tag.label}>
                      <IconButton
                        aria-label={`Filter ${tag.label} folders`}
                        onClick={() => selectTagFilter(tag.value)}
                        sx={{
                          width: 38,
                          height: 38,
                          border: 2,
                          borderColor: activeFilter === tag.value ? 'primary.main' : 'transparent',
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: tag.value,
                            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>
              </Menu>
            </Stack>
          </Stack>

          {selectionMode ? (
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.25,
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <CheckBoxOutlinedIcon color="primary" fontSize="small" />
                  <Typography fontWeight={900} noWrap>
                    {selectedEntries.length ? `${selectedEntries.length} selected` : 'Selection mode'}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {selectedFolderEntries.length} folders, {selectedFileEntries.length} files
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                }}
              >
                <Button
                  size="small"
                  startIcon={<DoneAllIcon />}
                  variant="outlined"
                  disabled={!visibleEntryKeys.length || allVisibleSelected}
                  onClick={handleSelectAllVisible}
                >
                  Select all visible
                </Button>
                <Button
                  size="small"
                  startIcon={<DriveFileMoveIcon />}
                  variant="outlined"
                  disabled={!selectedEntries.length}
                  onClick={() => setBulkMoveOpen(true)}
                >
                  Move
                </Button>
                {selectedFolderEntries.length ? (
                  <Button
                    size="small"
                    startIcon={<SellIcon />}
                    variant="outlined"
                    onClick={() => setBulkTagOpen(true)}
                  >
                    Tag folders
                  </Button>
                ) : null}
                <Button
                  size="small"
                  startIcon={<PushPinIcon />}
                  variant="outlined"
                  disabled={!selectedEntries.length}
                  onClick={handleBulkFavorite}
                >
                  {allSelectedAreFavorites ? 'Unpin' : 'Pin'}
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  color="error"
                  variant="outlined"
                  disabled={!selectedEntries.length}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={clearSelection}
                >
                  Done
                </Button>
              </Stack>
            </Paper>
          ) : null}

          {showFavoritesSection ? (
            <FavoritesSection
              entries={favoriteEntries}
              viewMode={settings.viewMode}
              gridSize={settings.gridSize}
              open={settings.favoritesOpen !== false}
              onToggleOpen={() => updateSettings({ favoritesOpen: settings.favoritesOpen === false })}
              onOpen={handleEntryAction}
              getMenuProps={getMenuProps}
              selectionMode={selectionMode}
              selectedKeySet={selectedKeySet}
              onEntryLongPress={(entry) => toggleEntrySelection(entry)}
              onToggleSelection={toggleEntrySelection}
              onVisibleEntriesChange={setVisibleFavoriteEntries}
            />
          ) : null}

          <Stack spacing={1.25}>
            <Typography variant="h6" fontWeight={900} sx={{ userSelect: 'none' }}>
              {sectionTitle}
            </Typography>
            {entries.length ? (
              <ExplorerItems
                entries={entries}
                viewMode={settings.viewMode}
                gridSize={settings.gridSize}
                onOpen={handleEntryAction}
                getMenuProps={getMenuProps}
                selectionMode={selectionMode}
                selectedKeySet={selectedKeySet}
                onEntryLongPress={(entry) => toggleEntrySelection(entry)}
                onToggleSelection={toggleEntrySelection}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  py: 8,
                  px: 2,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  borderStyle: 'dashed',
                }}
              >
                <AddIcon color="primary" sx={{ fontSize: 42, mb: 1 }} />
                <Typography variant="h6" fontWeight={800}>
                  Nothing here yet
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Create a folder or text file in {currentFolder?.name || 'this folder'}.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ justifyContent: 'center' }}
                >
                  <Button variant="contained" onClick={() => openNameDialog('createFolder')}>
                    New Folder
                  </Button>
                  <Button variant="outlined" onClick={() => openNameDialog('createFile')}>
                    New File
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Stack>
      </Container>

      <NameDialog
        open={Boolean(nameDialog)}
        title={nameDialog?.title || ''}
        label={nameDialog?.label || 'Name'}
        initialValue={nameDialog?.initialValue || ''}
        validate={validateDialogName}
        onSubmit={handleNameSubmit}
        onClose={() => setNameDialog(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete ${deleteTarget?.type === 'folder' ? 'Folder' : 'File'}`}
        description={
          deleteTarget?.type === 'folder'
            ? `Delete ${deleteTarget.item.name} and everything inside it? This cannot be undone.`
            : `Delete ${getFileDisplayName(deleteTarget?.item || { name: '' })}? This cannot be undone.`
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <MoveDialog
        open={Boolean(moveTarget)}
        item={moveTarget?.item}
        itemType={moveTarget?.type}
        currentParentId={
          moveTarget?.type === 'folder'
            ? moveTarget.item.parentId
            : moveTarget?.item.parentFolderId
        }
        onMove={(destinationId) => {
          if (moveTarget?.type === 'folder') {
            moveFolder(moveTarget.item.id, destinationId);
          } else if (moveTarget?.type === 'file') {
            moveFile(moveTarget.item.id, destinationId);
          }
        }}
        onClose={() => setMoveTarget(null)}
      />

      <MoveDialog
        open={bulkMoveOpen}
        item={{ name: `${selectedEntries.length} items` }}
        itemType="selection"
        initialSelectedId={bulkMoveInitialFolderId}
        disabledIds={bulkMoveDisabledIds}
        title={`Move ${selectedEntries.length || 0} Selected Items`}
        onMove={handleBulkMove}
        onClose={() => setBulkMoveOpen(false)}
      />

      <DownloadDialog
        open={Boolean(downloadTarget)}
        item={downloadTarget?.item}
        itemType={downloadTarget?.type}
        onClose={() => setDownloadTarget(null)}
      />

      <TagDialog
        open={Boolean(tagTarget)}
        value={tagTarget?.tagColor || defaultTagColor}
        colors={availableTagColors}
        defaultColor={defaultTagColor}
        onSelect={(color, options = {}) => {
          if (tagTarget) {
            setFolderTag(tagTarget.id, color, options);
            setTagTarget({
              ...tagTarget,
              tagColor: options.useDefault ? defaultTagColor : color,
              usesDefaultTag: Boolean(options.useDefault),
            });
          }
        }}
        onClose={() => setTagTarget(null)}
      />

      <TagDialog
        open={bulkTagOpen}
        value={bulkTagValue}
        colors={availableTagColors}
        defaultColor={defaultTagColor}
        onSelect={(color, options = {}) => {
          setFolderTags(
            selectedFolderEntries.map((entry) => entry.item.id),
            color,
            options
          );
        }}
        onClose={() => setBulkTagOpen(false)}
      />

      <WorkspaceSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <AppearanceDialog
        open={Boolean(appearanceFile)}
        file={appearanceFile}
        onChange={(appearancePatch) => {
          if (appearanceFile) {
            updateFileAppearance(appearanceFile.id, appearancePatch);
          }
        }}
        onReset={() => {
          if (appearanceFile) {
            resetFileAppearanceToDefault(appearanceFile.id);
          }
        }}
        onClose={() => setAppearanceFileId(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete Selected Items"
        description={`Delete ${selectedEntries.length} selected item${selectedEntries.length === 1 ? '' : 's'}? This cannot be undone.`}
        onConfirm={handleBulkDelete}
        onClose={() => setBulkDeleteOpen(false)}
      />
    </AppLayout>
  );
}
